/**
 * Anomaly detection parameters form with progressive disclosure.
 *
 * Implements Markus's spec from `anomaly_detection_1.py` L729-785:
 *  - 5 group headings identical to Python comments (L731/745/756/764/771)
 *  - DEC visible only after GAP_MAX has a value
 *  - STL.PERIOD_H visible only after STL.run is checked (default 24)
 *  - LSTM 4 fields visible only after LSTM.run is checked (defaults 1/64/10/16)
 *  - No "* optional" markers on this page
 *  - On-blur validation routed through `useParameterValidation`
 */
import React, { useCallback, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

import CheckBox from '@/shared/components/buttons/CheckBox';
import { ANOMALY_DEFAULTS, AnomalyParameters, Lang } from '../types';
import { useParameterValidation } from '../useParameterValidation';

interface Props {
  parameters: AnomalyParameters;
  onChange: (next: AnomalyParameters) => void;
  lang: Lang;
  uploadId: string | null;
  onParamShapeError?: (err: Error) => void;
  disabled?: boolean;
  onValidationStateChange?: (hasErrors: boolean) => void;
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  error?: string;
  placeholder?: string;
  testId?: string;
  disabled?: boolean;
}

const Field: React.FC<FieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  testId,
  disabled,
}) => {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  return (
    <div className="flex flex-col">
      <label
        htmlFor={inputId}
        className="mb-1 text-xs sm:text-sm md:text-base font-medium text-foreground"
      >
        {label}
      </label>
      <input
        id={inputId}
        type="number"
        data-testid={testId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={
          'rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 ' +
          (error
            ? 'border-red-500 focus:ring-red-300'
            : 'border-input focus:ring-ring')
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
      />
      {error && (
        <p
          id={errorId}
          data-testid={testId ? `${testId}-error` : undefined}
          aria-live="polite"
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Convert raw string input to a value safe for backend validation:
 *  - empty → null (means "not set")
 *  - non-numeric (NaN/Infinity from `Number('abc')`) → undefined,
 *    signaling the form to skip the round-trip and keep the prior error.
 * Plain numbers pass through.
 */
function toBackendValue(raw: string): number | null | undefined {
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="mt-8 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </h4>
);

const AnomalyParametersSection: React.FC<Props> = ({
  parameters,
  onChange,
  lang,
  uploadId,
  onParamShapeError,
  disabled,
  onValidationStateChange,
}) => {
  const { t } = useTranslation('dataProcessing');
  const { errors, validate } = useParameterValidation(parameters, lang, uploadId, {
    onShapeError: onParamShapeError,
  });

  useEffect(() => {
    onValidationStateChange?.(Object.keys(errors).length > 0);
  }, [errors, onValidationStateChange]);

  // Convenience updaters keep the immutable update boilerplate out of JSX.
  const update = useCallback(
    (patch: Partial<AnomalyParameters>) => onChange({ ...parameters, ...patch }),
    [parameters, onChange],
  );

  // Bridge raw input → debounced backend validation. Skip if user typed
  // garbage that Number() can't parse — keeps stale error visible.
  const blurValidate = useCallback(
    (name: string, raw: string) => {
      const v = toBackendValue(raw);
      if (v === undefined) return;
      validate(name, v);
    },
    [validate],
  );
  const updateSbad = useCallback(
    (patch: Partial<AnomalyParameters['sbad']>) =>
      onChange({ ...parameters, sbad: { ...parameters.sbad, ...patch } }),
    [parameters, onChange],
  );
  const updateStl = useCallback(
    (patch: Partial<AnomalyParameters['stl']>) =>
      onChange({ ...parameters, stl: { ...parameters.stl, ...patch } }),
    [parameters, onChange],
  );
  const updateLstm = useCallback(
    (patch: Partial<AnomalyParameters['lstm']>) =>
      onChange({ ...parameters, lstm: { ...parameters.lstm, ...patch } }),
    [parameters, onChange],
  );

  const showDec = parameters.gapMax !== '' && parameters.gapMax != null;

  return (
    <div className="pt-8 mt-8 border-t border-border">
      {/* Group 1 — TIME-BASED THRESHOLDS (Python L731) */}
      <SectionHeading>{t('anomaly.groups.timeBased')}</SectionHeading>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <Field
          label={t('anomaly.labels.eqMax')}
          value={parameters.eqMax}
          onChange={(v) => update({ eqMax: v })}
          onBlur={() => blurValidate('EQ_MAX', parameters.eqMax)}
          error={errors.EQ_MAX}
          testId="anomaly-eqMax"
          disabled={disabled}
        />
        <Field
          label={t('anomaly.labels.lgMin')}
          value={parameters.lgMin}
          onChange={(v) => update({ lgMin: v })}
          onBlur={() => blurValidate('LG_MIN', parameters.lgMin)}
          error={errors.LG_MIN}
          testId="anomaly-lgMin"
          disabled={disabled}
        />
        <Field
          label={t('anomaly.labels.gapMax')}
          value={parameters.gapMax}
          onChange={(v) =>
            // When GAP_MAX is cleared, DEC becomes irrelevant — reset it to
            // default to avoid sending a stale value to the backend after the
            // field unmounts (orphan-state regression).
            update(
              v === ''
                ? { gapMax: v, dec: ANOMALY_DEFAULTS.dec }
                : { gapMax: v },
            )
          }
          onBlur={() => blurValidate('GAP_MAX', parameters.gapMax)}
          error={errors.GAP_MAX}
          testId="anomaly-gapMax"
          disabled={disabled}
        />
        {showDec && (
          <Field
            label={t('anomaly.labels.dec')}
            value={parameters.dec}
            onChange={(v) => update({ dec: v })}
            onBlur={() => blurValidate('DEC', parameters.dec)}
            error={errors.DEC}
            testId="anomaly-dec"
            disabled={disabled}
          />
        )}
      </div>

      {/* Group 2 — VALUE LIMITS (Python L745) */}
      <SectionHeading>{t('anomaly.groups.valueLimits')}</SectionHeading>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <Field
          label={t('anomaly.labels.vMax')}
          value={parameters.vMax}
          onChange={(v) => update({ vMax: v })}
          onBlur={() => blurValidate('V_MAX', parameters.vMax)}
          error={errors.V_MAX}
          testId="anomaly-vMax"
          disabled={disabled}
        />
        <Field
          label={t('anomaly.labels.vMin')}
          value={parameters.vMin}
          onChange={(v) => update({ vMin: v })}
          onBlur={() => blurValidate('V_MIN', parameters.vMin)}
          error={errors.V_MIN}
          testId="anomaly-vMin"
          disabled={disabled}
        />
      </div>
      <div className="mt-4">
        <CheckBox
          label={t('anomaly.labels.el0')}
          checked={parameters.el0}
          onChange={(checked: boolean) => update({ el0: checked })}
          disabled={disabled}
        />
      </div>

      {/* Group 3 — SLOPE-BASED ANOMALY DETECTION (Python L756) */}
      <SectionHeading>{t('anomaly.groups.sbad')}</SectionHeading>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <Field
          label={t('anomaly.labels.sbadChgMax')}
          value={parameters.sbad.chgMax}
          onChange={(v) => updateSbad({ chgMax: v })}
          onBlur={() => blurValidate('SBAD.CHG_MAX', parameters.sbad.chgMax)}
          error={errors['SBAD.CHG_MAX']}
          testId="anomaly-sbad-chgMax"
          disabled={disabled}
        />
        <Field
          label={t('anomaly.labels.sbadLgMax')}
          value={parameters.sbad.lgMax}
          onChange={(v) => updateSbad({ lgMax: v })}
          onBlur={() => blurValidate('SBAD.LG_MAX', parameters.sbad.lgMax)}
          error={errors['SBAD.LG_MAX']}
          testId="anomaly-sbad-lgMax"
          disabled={disabled}
        />
      </div>

      {/* Group 4 — STL (Python L764) */}
      <SectionHeading>{t('anomaly.groups.stl')}</SectionHeading>
      <div className="mb-3">
        <CheckBox
          label={t('anomaly.labels.stlRun')}
          checked={parameters.stl.run}
          onChange={(checked: boolean) => updateStl({ run: checked })}
          disabled={disabled}
        />
      </div>
      {parameters.stl.run && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label={t('anomaly.labels.stlPeriodH')}
            value={parameters.stl.periodH}
            onChange={(v) => updateStl({ periodH: v })}
            onBlur={() => blurValidate('STL.PERIOD_H', parameters.stl.periodH)}
            error={errors['STL.PERIOD_H']}
            testId="anomaly-stl-periodH"
            disabled={disabled}
          />
        </div>
      )}

      {/* Group 5 — LSTM (Python L771) */}
      <SectionHeading>{t('anomaly.groups.lstm')}</SectionHeading>
      <div className="mb-3">
        <CheckBox
          label={t('anomaly.labels.lstmRun')}
          checked={parameters.lstm.run}
          onChange={(checked: boolean) => updateLstm({ run: checked })}
          disabled={disabled}
        />
      </div>
      {parameters.lstm.run && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label={t('anomaly.labels.lstmPeriodH')}
            value={parameters.lstm.periodH}
            onChange={(v) => updateLstm({ periodH: v })}
            onBlur={() => blurValidate('LSTM.PERIOD_H', parameters.lstm.periodH)}
            error={errors['LSTM.PERIOD_H']}
            testId="anomaly-lstm-periodH"
            disabled={disabled}
          />
          <Field
            label={t('anomaly.labels.lstmNeurons')}
            value={parameters.lstm.neurons}
            onChange={(v) => updateLstm({ neurons: v })}
            onBlur={() => blurValidate('LSTM.NEURONS', parameters.lstm.neurons)}
            error={errors['LSTM.NEURONS']}
            testId="anomaly-lstm-neurons"
            disabled={disabled}
          />
          <Field
            label={t('anomaly.labels.lstmEpochs')}
            value={parameters.lstm.epochs}
            onChange={(v) => updateLstm({ epochs: v })}
            onBlur={() => blurValidate('LSTM.EPOCHS', parameters.lstm.epochs)}
            error={errors['LSTM.EPOCHS']}
            testId="anomaly-lstm-epochs"
            disabled={disabled}
          />
          <Field
            label={t('anomaly.labels.lstmBatchSize')}
            value={parameters.lstm.batchSize}
            onChange={(v) => updateLstm({ batchSize: v })}
            onBlur={() => blurValidate('LSTM.BATCH_SIZE', parameters.lstm.batchSize)}
            error={errors['LSTM.BATCH_SIZE']}
            testId="anomaly-lstm-batchSize"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default AnomalyParametersSection;
